import React, { useCallback, useEffect } from 'react';
import { lazyForPaint } from 'react-loosely-lazy';
import { useQueryLoader } from 'react-relay';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import {
	AnalyticsSource,
	AnalyticsSubject,
} from '@atlassian/jira-analytics-web-react/src/components/decorators.tsx';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import ComponentWithAnalytics from '@atlassian/jira-analytics-web-react/src/utils/component-with-analytics.tsx';
import Breadcrumb from '@atlassian/jira-breadcrumbs/src/breadcrumb/component.tsx';
import Spinner from '@atlassian/jira-common-components-spinner/src/view.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import Placeholder from '@atlassian/jira-placeholder';
import { SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { getNextgenSettingsUrl } from '@atlassian/jira-project-settings-apps-common/src/urls';
import type { NativeConfigEnabledQuery } from '@atlassian/jira-relay/src/__generated__/NativeConfigEnabledQuery.graphql';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import { ServiceDeskDocumentTitle } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/document-title/index.tsx';
import type { TenantContext } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import AppContainer from '@atlassian/jira-spa-apps-project-settings-common/src/components/app-container';
import { NativeConfigEnabled } from '../common/graphql/NativeConfigEnabled';
import type { ErrorReason } from '../model/types';
import messages from './messages';
import { SpinnerContainer } from './styled';

export type Props = {
	isLoading: boolean;
	isInstalled: boolean;
	isEnabled: boolean;
	projectContext: ProjectContext | null;
	tenantContext: TenantContext;
	errorReason: ErrorReason;
	errorMessage: string;
	addonKey: string;
	onGetChatAppStatus: () => void;
	intl: IntlShape;
};

const Loading = () => (
	<SpinnerContainer>
		<Spinner />
	</SpinnerContainer>
);

export const LazyLoadedChatSettingsPage = lazyForPaint(
	() => import(/* webpackChunkName: "ChatSettingsPage" */ './ChatSettingsPage'),
);

export const ServiceDeskChatSettingsView = ({
	onGetChatAppStatus,
	isInstalled,
	isEnabled,
	isLoading,
	projectContext,
	tenantContext,
	errorMessage,
	errorReason,
	addonKey,
	intl,
}: Props) => {
	const [queryRef, loadQuery] = useQueryLoader<NativeConfigEnabledQuery>(NativeConfigEnabled);
	const refresh = useCallback(() => {
		if (projectContext?.projectId && tenantContext.activationId && tenantContext.activationId) {
			loadQuery({
				jiraProjectAri: JiraProjectAri.create({
					siteId: tenantContext.cloudId,
					projectId: projectContext.projectId.toString(),
				}).toString(),
			});
		}
	}, [loadQuery, tenantContext, projectContext]);
	useEffect(() => {
		onGetChatAppStatus();
		refresh();
	}, [onGetChatAppStatus, refresh]);

	const { formatMessage } = intl;
	if (isLoading || !projectContext || !tenantContext) {
		return <Loading />;
	}

	const { baseUrl } = tenantContext;
	const { projectKey, projectType, projectName, simplified } = projectContext;

	// render the default breadcrumb provided by AppContainer, for classic projects
	if (!simplified) {
		return (
			<>
				<ServiceDeskDocumentTitle projectName={projectName} section="chat" />
				<AppContainer baseUrl={baseUrl} projectKey={projectKey}>
					<ViewTracker />
					<Placeholder name="chat-settings-page" fallback={<Loading />}>
						{queryRef && (
							<LazyLoadedChatSettingsPage
								isInstalled={isInstalled}
								isEnabled={isEnabled}
								projectContext={projectContext}
								tenantContext={tenantContext}
								errorMessage={errorMessage}
								errorReason={errorReason}
								intl={intl}
								queryRef={queryRef}
								addonKey={addonKey}
							/>
						)}
					</Placeholder>
				</AppContainer>
			</>
		);
	}

	const channelsHref = `${getNextgenSettingsUrl(projectType, projectKey)}/channels`;

	const pageContent = (
		<>
			<ViewTracker />
			<Placeholder name="chat-settings-page" fallback={<Loading />}>
				{queryRef && (
					<LazyLoadedChatSettingsPage
						isInstalled={isInstalled}
						isEnabled={isEnabled}
						projectContext={projectContext}
						tenantContext={tenantContext}
						errorMessage={errorMessage}
						errorReason={errorReason}
						intl={intl}
						queryRef={queryRef}
						addonKey={addonKey}
					/>
				)}
			</Placeholder>
		</>
	);

	return (
		<>
			<ServiceDeskDocumentTitle projectName={projectName} section="chat" />
			<AppContainer baseUrl={baseUrl} projectKey={projectKey}>
				{fg('jsm_simplified_project_settings_navigation') ? (
					pageContent
				) : (
					<Breadcrumb
						breadcrumb={{
							text: formatMessage(messages.channelBreadcrumb),
							href: channelsHref,
							type: 'servicedeskChannelsBreadcrumb',
						}}
					>
						{pageContent}
					</Breadcrumb>
				)}
			</AppContainer>
		</>
	);
};

export default AnalyticsSubject('chatProjectSettings', {
	get referrer() {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const params = new URLSearchParams(document.location.search);
		return params?.get('referrer');
	},
})(
	AnalyticsSource(
		'chatProjectSettings',
		SCREEN,
	)(
		ComponentWithAnalytics('chatProjectSettingsInstall', {
			onGetChatAppStatus: 'onGetChatAppStatus',
		})(ServiceDeskChatSettingsView),
	),
);
